import { ITableData, IRowCells } from 'types/excelExport';
import { IProFormaSourceTableRow, IProFormaUses } from 'views/ProFormaTable/types';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from './constants';

// ============================================
// Parsing helpers
// ============================================

export const parseNumber = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isNaN(parsed) ? null : parsed;
};

export const getSourceMetricValue = (source: IProFormaSourceTableRow): number | null => {
    switch (source.metricType) {
        case DEVELOPMENT_SOURCE_METRIC.LTC:
            return parseNumber(source.loanToCost);
        case DEVELOPMENT_SOURCE_METRIC.LTV:
            return parseNumber(source.loanToValue);
        case DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD:
            return parseNumber(source.debtYield);
        default:
            return parseNumber(source.loanToCost);
    }
};

// ============================================
// Formatting helpers
// ============================================

export const formatCurrency = (value: number | null | undefined, showCents: boolean = false): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: showCents ? 2 : 0,
        maximumFractionDigits: showCents ? 2 : 0,
    }).format(value);
};

export const formatPercent = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return '';
    return `${(value * 100).toFixed(decimals)}%`;
};

export const formatNumber = (value: number | null | undefined, decimals: number = 0): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
};

export const formatMultiple = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return `${value.toFixed(2)}x`;
};

export const formatDate = (dateString: string | null | undefined, includeDay: boolean = false): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'long',
        ...(includeDay && { day: 'numeric' }),
        year: 'numeric',
    });
};

export const formatMonths = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return `${value} ${value === 1 ? 'month' : 'months'}`;
};

export const getMonthLabel = (startDate: string, monthIndex: number): string => {
    const date = new Date(startDate);
    date.setUTCMonth(date.getUTCMonth() + monthIndex);
    return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });
};

export const formatRateValue = (
    loanRateType: string | null | undefined,
    fixedRate: number | null | undefined,
): string => {
    if (loanRateType === 'variable') {
        return 'Variable';
    }
    return fixedRate != null ? formatPercent(fixedRate) : '';
};

// ============================================
// Data helpers
// ============================================

export const flattenUses = (
    uses: IProFormaUses[],
    level: number = 0,
    result: { use: IProFormaUses; level: number }[] = [],
): { use: IProFormaUses; level: number }[] => {
    uses.forEach(use => {
        result.push({ use, level });
        if (use.children && use.children.length > 0) {
            flattenUses(use.children, level + 1, result);
        }
    });
    return result;
};

// ============================================
// Row creation helpers
// ============================================

export const createHeaderRow = (
    cells: (string | number)[],
    color: string = EXCEL_COLORS.BLUE_HEADER,
    withAlignment: boolean = false,
): ITableData => ({
    rowCells: cells.map((value, index) => ({
        value,
        cellFont: { bold: true, color: { argb: EXCEL_COLORS.WHITE } },
        width: index === 0 ? 30 : 15,
        ...(withAlignment && { alignment: { horizontal: index === 0 ? 'left' : 'right' } }),
    })),
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: color },
    },
});

export const createDataRow = (cells: IRowCells[], bold: boolean = false): ITableData => ({
    rowCells: cells.map((cell, index) => ({
        ...cell,
        cellFont: bold ? { bold: true } : cell.cellFont,
        alignment: cell.alignment || { horizontal: index === 0 ? 'left' : 'right' },
    })),
});

export const createSectionHeaderRow = (label: string, colSpan: number = 1): ITableData => ({
    rowCells: [
        { value: label, cellFont: { bold: true }, width: 30 },
        ...Array(colSpan - 1).fill({ value: '' }),
    ],
    rowFill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_COLORS.GRAY_HEADER },
    },
});

export const createBlankRow = (colCount: number = 1): ITableData => ({
    rowCells: Array(colCount).fill({ value: '' }),
});

export const createLabelValueRow = (
    label: string,
    value: string | number | null | undefined,
    centerValue: boolean = false,
    numFmt?: string,
): ITableData => {
    const isNumericFormat = Boolean(numFmt);
    const displayValue = value ?? (isNumericFormat ? 0 : '');

    return createDataRow([
        { value: label, width: 30, alignment: { horizontal: 'left' } },
        {
            value: displayValue,
            width: 20,
            alignment: { horizontal: centerValue ? 'center' : 'right' },
            numFmt: numFmt && typeof displayValue === 'number' ? numFmt : undefined,
        },
    ]);
};

export const createMetricRow = (
    label: string,
    values: (number | null | undefined)[],
    numFmt?: string,
): ITableData => createDataRow([
    { value: label, width: 30 },
    ...values.map(v => ({
        value: v ?? 0,
        width: 15,
        alignment: { horizontal: 'right' as const },
        numFmt,
    })),
]);

export const createTripleValueRow = (
    label: string,
    untrended: number | null | undefined,
    trended: number | null | undefined,
    atSale: number | null | undefined,
    numFmt: string = EXCEL_NUM_FORMATS.CURRENCY,
    bold: boolean = false,
): ITableData => createDataRow([
    { value: label, width: 30, cellFont: bold ? { bold: true } : undefined },
    { value: untrended ?? 0, width: 15, alignment: { horizontal: 'right' }, numFmt },
    { value: trended ?? 0, width: 15, alignment: { horizontal: 'right' }, numFmt },
    { value: atSale ?? 0, width: 15, alignment: { horizontal: 'right' }, numFmt },
], bold);
